import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Phone,
  Instagram,
  Menu,
  X,
  HeartPulse,
  Activity,
  Stethoscope,
  Waves,
  Cpu,
  Baby,
  HeartHandshake,
  Syringe,
  GraduationCap,
  Award,
  ScrollText,
  
  ArrowRight,
  CalendarCheck,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { WhatsappIcon, FloatingWhatsapp } from "@/components/whatsapp-icon";
import logo__url from "@/assets/logo-rafaela.jpg";
const logo = { url: logo__url };
import heroBg from "@/assets/hero-bg.jpg";
import heroBgAvif from "@/assets/hero-bg.jpg?format=avif&as=url";
import heroBgWebp from "@/assets/hero-bg.jpg?format=webp&as=url";
import portraitAsset__url from "@/assets/dra-rafaela-hero.jpg";
import portraitAvif from "@/assets/dra-rafaela-hero.jpg?format=avif&as=url";
import portraitWebp from "@/assets/dra-rafaela-hero.jpg?format=webp&as=url";
const portraitAsset = { url: portraitAsset__url };
import sobreAsset__url from "@/assets/dra-rafaela-sobre.jpg";
const sobreAsset = { url: sobreAsset__url };
import consultaAsset__url from "@/assets/dra-rafaela-consulta.jpg";
const consultaAsset = { url: consultaAsset__url };
import consulta from "@/assets/consulta.jpg";
import centro from "@/assets/centro-cirurgico.jpg";
import agenda1__url from "@/assets/agenda-1.jpg";
const agenda1 = { url: agenda1__url };
import agenda2__url from "@/assets/agenda-2.jpg";
const agenda2 = { url: agenda2__url };
import agenda3__url from "@/assets/agenda-3.jpg";
const agenda3 = { url: agenda3__url };
import { blogSummaries } from "@/lib/blog-summaries";
import { AgendamentoSection } from "@/components/site-chrome";

// Preload the LCP hero portrait as early as possible (AVIF, WebP fallback).
if (typeof document !== "undefined") {
  const supportsAvif = document
    .createElement("canvas")
    .toDataURL("image/avif")
    .startsWith("data:image/avif");
  const href = supportsAvif ? portraitAvif : portraitWebp;
  const type = supportsAvif ? "image/avif" : "image/webp";
  if (!document.querySelector(`link[rel="preload"][href="${href}"]`)) {
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = href;
    link.type = type;
    (link as HTMLLinkElement & { fetchPriority: string }).fetchPriority = "high";
    document.head.appendChild(link);
  }
}

export const Route = createFileRoute("/")({
  component: Index,
});

const WHATSAPP_URL = "https://wa.me/5582993728868";
const PHONE_DISPLAY = "+55 (82) 99372-8868";
const PHONE_TEL = "tel:+5582993728868";
const INSTAGRAM_URL = "https://www.instagram.com/drarafaelasales";
const MAPS_URL = "https://maps.app.goo.gl/CeLbLZiavK9WSePB9";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "Sobre mim", href: "#sobre" },
  { label: "Quando me procurar", href: "#quando" },
  { label: "Áreas de atuação", href: "#areas" },
  { label: "A consulta", href: "#consulta" },
  { label: "Blog", href: "#blog" },
  { label: "Agendamento", href: "#agendamento" },
];

function CtaWhats({ className = "", label = "AGENDAR AVALIAÇÃO" }: { className?: string; label?: string }) {
  return (
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-xs font-medium uppercase tracking-[0.18em] text-primary-foreground shadow-sm transition-all hover:bg-deep hover:shadow-md ${className}`}
    >
      <WhatsappIcon className="h-4 w-4" />
      {label}
    </a>
  );
}

function Index() {
  const [open, setOpen] = useState(false);

  return (
    <div id="home" className="flex min-h-screen flex-col bg-background text-foreground">
      <FloatingWhatsapp href={WHATSAPP_URL} />
      {/* ===== Header ===== */}
      <header className="sticky top-0 z-50 bg-background/95 shadow-[0_1px_0_0_rgba(0,0,0,0.04)] backdrop-blur-md">
        {/* FAIXA 1 — topo institucional: contatos | logo | redes */}
        <div className="mx-auto grid max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-4 px-5 py-4 md:px-8 md:py-5">
          {/* Contatos à esquerda */}
          <div className="hidden flex-col gap-2 text-[0.78rem] text-muted-foreground md:flex">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition-colors hover:text-primary">
              <WhatsappIcon className="h-3.5 w-3.5 shrink-0" /> {PHONE_DISPLAY}
            </a>
            <a href={PHONE_TEL} className="inline-flex items-center gap-2 transition-colors hover:text-primary">
              <Phone className="h-3.5 w-3.5 shrink-0" /> {PHONE_DISPLAY}
            </a>
          </div>

          {/* Logo central — protagonista */}
          <a href="#home" className="flex justify-center">
            <img
              src={logo.url}
              alt="Logo Dra. Rafaela Sales — Cirurgia Cardíaca"
              className="h-16 w-auto sm:h-18 md:h-20"
            />
          </a>

          {/* Redes à direita + menu mobile */}
          <div className="flex items-center justify-end gap-3">
            <a
              aria-label="Instagram"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden h-10 w-10 items-center justify-center rounded-full border border-foreground/25 text-foreground transition-colors hover:border-primary hover:text-primary md:flex"
            >
              <Instagram className="h-4 w-4" />
            </a>
            <a
              aria-label="WhatsApp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden h-10 w-10 items-center justify-center rounded-full border border-foreground/25 text-foreground transition-colors hover:border-primary hover:text-primary md:flex"
            >
              <WhatsappIcon className="h-4 w-4" />
            </a>
            {/* WhatsApp sempre visível no mobile */}
            <a
              aria-label="WhatsApp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-foreground/25 text-foreground md:hidden"
            >
              <WhatsappIcon className="h-4 w-4" />
            </a>
            <button
              aria-label="Abrir menu"
              className="rounded-full p-2 text-foreground lg:hidden"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* divisória fina suave */}
        <div className="mx-auto h-px max-w-6xl bg-border/60" />

        {/* FAIXA 2 — menu de navegação (barra fina) */}
        <nav className="mx-auto hidden max-w-6xl items-center justify-center gap-8 px-6 py-2.5 lg:flex">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-[0.72rem] uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-primary"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {open && (
          <nav className="border-t border-border/60 bg-background px-5 pb-5 pt-2 lg:hidden">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="block border-b border-border/40 py-3 text-sm uppercase tracking-[0.12em] text-muted-foreground"
              >
                {l.label}
              </a>
            ))}
          </nav>
        )}
      </header>



      <main className="flex-1">
        {/* ===== BLOCO 01 — HERO ===== */}
        <section className="relative overflow-hidden">
          <picture>
            <source srcSet={heroBgAvif} type="image/avif" />
            <source srcSet={heroBgWebp} type="image/webp" />
            <img
              src={heroBg}
              alt=""
              aria-hidden="true"
              width={1280}
              height={896}
              loading="lazy"
              decoding="async"
              {...{ fetchpriority: "low" }}
              className="absolute inset-0 h-full w-full object-cover opacity-25"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/50" />
          <div className="relative mx-auto grid max-w-6xl items-center gap-8 px-6 py-10 md:grid-cols-2 md:py-12">
            <div className="text-center md:text-left">
              <picture>
                <source srcSet={portraitAvif} type="image/avif" />
                <source srcSet={portraitWebp} type="image/webp" />
                <img
                  src={portraitAsset.url}
                  alt="Dra. Rafaela Sales"
                  width={896}
                  height={1152}
                  decoding="async"
                  {...{ fetchpriority: "high" }}
                  className="mb-6 aspect-[4/5] max-h-[55vh] w-full rounded-[2rem] object-cover object-top shadow-xl md:hidden"
                />
              </picture>
              <p className="mb-3 text-xs uppercase tracking-[0.3em] text-primary">Cirurgiã Cardíaca em Maceió — AL</p>
              <h1 className="font-sans text-3xl leading-[1.1] text-foreground sm:text-4xl md:text-[2.6rem]">
                Técnica, precisão e cuidado humano em cirurgia cardíaca
              </h1>
              <p className="mt-4 mx-auto max-w-md text-[0.95rem] leading-relaxed text-muted-foreground md:mx-0">
                Atuação em cirurgia de revascularização do miocárdio, procedimentos percutâneos, TAVI, doenças valvares,
                aneurismas da aorta, cardiopatias congênitas no adulto e dispositivos cardíacos implantáveis.
              </p>
              <div className="mt-6 flex justify-center md:justify-start">
                <CtaWhats />
              </div>
            </div>

            <div className="relative hidden md:block">
              <div className="absolute -right-4 -top-4 h-full w-full rounded-[2rem] border border-rose/40" aria-hidden="true" />
              <picture>
                <source srcSet={portraitAvif} type="image/avif" />
                <source srcSet={portraitWebp} type="image/webp" />
                <img
                  src={portraitAsset.url}
                  alt="Dra. Rafaela Sales"
                  width={896}
                  height={1152}
                  decoding="async"
                  {...{ fetchpriority: "high" }}
                  className="relative aspect-[4/5] max-h-[60vh] w-full rounded-[2rem] object-cover object-top shadow-xl"
                />
              </picture>
            </div>
          </div>
        </section>

        {/* ===== BLOCO 02 — SOBRE ===== */}
        <section id="sobre" className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="grid items-start gap-12 md:grid-cols-2">
            <div className="md:sticky md:top-28">
              <img
                src={sobreAsset.url}
                alt="Dra. Rafaela Sales"
                width={1024}
                height={1280}
                loading="lazy"
                className="aspect-square w-full rounded-[2rem] object-cover shadow-lg"
              />
            </div>
            <div>
              <div className="text-center md:text-left">

                <p className="text-xs uppercase tracking-[0.3em] text-primary">Cirurgiã Cardíaca</p>
                <h2 className="mt-3 font-display text-4xl text-foreground md:text-5xl">Dra. Rafaela Sales</h2>
                <p className="mt-2 text-sm tracking-[0.12em] text-muted-foreground">
                  CRM-AL: 6324 &nbsp;|&nbsp; RQE: 4240
                </p>
              </div>


              <div className="mt-6 space-y-4 border-l-2 border-primary pl-5 text-left text-[0.98rem] leading-relaxed text-muted-foreground md:border-l-0 md:pl-0">
                <p>
                  Sou médica formada pela Universidade Federal de Alagoas (UFAL) em 2013 e concluí minha residência em
                  Cirurgia Cardiovascular na Santa Casa de Misericórdia de Maceió.
                </p>
                <p>
                  Minha formação foi construída com foco em precisão técnica, responsabilidade e cuidado centrado no
                  paciente.
                </p>
                <p>
                  Obtive o Título de Especialista em Cirurgia Cardiovascular pela Sociedade Brasileira de Cirurgia
                  Cardiovascular (SBCCV) em 2018, aos 27 anos, sendo uma das mais jovens cirurgiãs tituladas do país.
                </p>
                <p>
                  Atuo também na formação de novos médicos, como preceptora do internato de Medicina do CESMAC e da
                  Residência de Cirurgia Cardiovascular da Santa Casa de Misericórdia de Maceió.
                </p>
                <p>
                  Minha prática é guiada pela atualização constante, atenção aos detalhes e compromisso com cada etapa do
                  cuidado humanizado.
                </p>
              </div>
            </div>
          </div>

          {/* Cards de formação */}
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: GraduationCap, text: "Graduada em Medicina pela Universidade Federal de Alagoas (UFAL)" },
              { icon: Stethoscope, text: "Residência em Cirurgia Cardiovascular pela Santa Casa de Misericórdia de Maceió" },
              { icon: Award, text: "Título de Especialista em Cirurgia Cardiovascular pela SBCCV/AMB" },
              { icon: HeartPulse, text: "Extensão em Cardiopatias Estruturais com Ênfase em TAVI — Porto Alegre/RS" },
              { icon: ScrollText, text: "Pós-graduação em Gestão de Serviços de Saúde pela Fundação Getulio Vargas (FGV)" },
              { icon: Award, text: "Aprovada em concurso público para Cirurgia Cardíaca pela Secretaria de Estado da Saúde de Alagoas" },
            ].map((c, i) => (
              <div key={i} className="flex gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-primary">
                  <c.icon className="h-5 w-5" />
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">{c.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ===== BLOCO 03 — QUANDO ME PROCURAR ===== */}
        <section id="quando" className="bg-secondary/70">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.3em] text-primary">Guia rápido</p>
              <h2 className="mt-3 font-display text-4xl text-foreground md:text-5xl">Quando me procurar?</h2>
            </div>

            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  icon: HeartPulse,
                  title: "Alterações nas válvulas do coração",
                  text: "Quando há diagnóstico de estenose, insuficiência valvar, sopro importante ou indicação de avaliação cirúrgica.",
                },
                {
                  icon: Activity,
                  title: "Revascularização do Miocárdio",
                  text: "Quando há obstruções nas artérias do coração e necessidade de avaliação para cirurgia de revascularização.",
                },
                {
                  icon: Waves,
                  title: "Aneurisma da aorta",
                  text: "Quando há dilatação da aorta identificada em exames de imagem e necessidade de acompanhamento especializado.",
                },
                {
                  icon: Stethoscope,
                  title: "Indicação de TAVI",
                  text: "Pacientes com estenose da válvula aórtica que precisam avaliar a possibilidade de tratamento menos invasivo.",
                },
                {
                  icon: HeartHandshake,
                  title: "Fechamento Percutâneo de CIA e FOP",
                  text: "Quando há diagnóstico de comunicação interatrial ou forame oval patente e necessidade de avaliar tratamento por cateterismo.",
                },
                {
                  icon: Cpu,
                  title: "Alterações no ritmo cardíaco",
                  text: "Situações em que pode haver indicação de marcapasso, CDI ou ressincronizador cardíaco.",
                },
                {
                  icon: Syringe,
                  title: "Procedimentos percutâneos cardíacos",
                  text: "Avaliação de procedimentos minimamente invasivos para condições estruturais do coração, conforme indicação individualizada.",
                },
                {
                  icon: Baby,
                  title: "Cardiopatias congênitas no adulto",
                  text: "Condições cardíacas presentes desde o nascimento que precisam de acompanhamento ou tratamento na vida adulta.",
                },
              ].map((c, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-transform hover:-translate-y-1"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose/15 text-rose">
                    <c.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-sans font-bold text-2xl text-foreground">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex justify-center">
              <div className="flex max-w-xl items-center gap-3 rounded-full border border-rose/30 bg-rose/10 px-6 py-3.5 text-left md:text-center sm:gap-4">
                
                <p className="text-pretty text-xs uppercase leading-relaxed tracking-[0.15em] text-foreground/80">
                  Em caso de sintomas intensos ou súbitos, procure atendimento de urgência.
                </p>
              </div>
            </div>

          </div>
        </section>

        {/* ===== BLOCO 04 — ÁREAS DE ATUAÇÃO ===== */}
        <section id="areas" className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">Especialidades</p>
            <h2 className="mt-3 font-display text-4xl text-foreground md:text-5xl">Principais áreas de atuação</h2>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Activity, title: "Cirurgia de Revascularização do Miocárdio", text: "Tratamento para obstruções nas artérias do coração, com objetivo de restabelecer o fluxo sanguíneo e reduzir riscos futuros." },
              { icon: Stethoscope, title: "TAVI — Implante Valvar Aórtico Transcateter", text: "Procedimento minimamente invasivo para tratar a estenose da válvula aórtica em pacientes selecionados." },
              { icon: HeartPulse, title: "Fechamento Percutâneo de Shunts Intracardíacos", text: "Procedimentos minimamente invasivos para fechamento de defeitos congênitos, como comunicação interatrial e forame oval patente." },
              { icon: HeartPulse, title: "Cirurgias Valvares", text: "Tratamentos indicados para alterações no funcionamento das válvulas cardíacas, podendo envolver reparo ou substituição." },
              { icon: Waves, title: "Aneurismas da Aorta", text: "Avaliação e tratamento de dilatações na principal artéria do corpo, com abordagem definida de forma individualizada." },
              { icon: Stethoscope, title: "Endocardite Infecciosa", text: "Tratamento de infecções que acometem estruturas do coração, podendo incluir suporte clínico e cirurgia." },
              { icon: Cpu, title: "Implante de Dispositivos Cardíacos", text: "Marcapasso, CDI e ressincronizador cardíaco para controle do ritmo e suporte em casos selecionados." },
              { icon: Baby, title: "Cardiopatias Congênitas no Adulto", text: "Avaliação de condições cardíacas presentes desde o nascimento e que podem exigir tratamento na vida adulta." },
            ].map((c, i) => (
              <div key={i} className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <c.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-sans font-bold text-xl leading-tight text-foreground">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <CtaWhats />
          </div>
        </section>

        {/* ===== BLOCO 05 — A CONSULTA ===== */}
        <section id="consulta" className="bg-rose/10">
          <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
            <div className="grid items-center gap-12 md:grid-cols-2">
              <div>
                <div className="text-center md:text-left">

                  <p className="text-xs uppercase tracking-[0.3em] text-rose">Sobre a consulta médica</p>
                  <h2 className="mt-3 font-display text-3xl text-foreground md:text-4xl">
                    Como é a consulta com a Dra. Rafaela?
                  </h2>
                </div>
                <div className="mt-6 space-y-4 border-l-2 border-primary pl-5 text-left text-[0.98rem] leading-relaxed text-muted-foreground md:border-l-0 md:pl-0">
                  <p>
                    A avaliação começa com a escuta da história clínica, análise dos sintomas, exames anteriores e
                    histórico de saúde do paciente.
                  </p>
                  <p>
                    A partir disso, a Dra. Rafaela orienta os próximos passos de forma individualizada, explicando
                    possibilidades de acompanhamento, investigação ou tratamento cirúrgico quando houver indicação.
                  </p>
                  <p>
                    O objetivo é que o paciente e sua família compreendam o quadro com segurança, recebendo uma
                    orientação clara, técnica e humanizada.
                  </p>
                </div>
              </div>
              <img
                src={consultaAsset.url}
                alt="Dra. Rafaela Sales"
                width={1024}
                height={1024}
                loading="lazy"
                className="aspect-square w-full rounded-[2rem] object-cover shadow-lg"
              />
            </div>

            <h3 className="mt-16 text-center font-display text-2xl uppercase tracking-[0.15em] text-foreground">
              Modalidades de atendimento
            </h3>
            <div className="mx-auto mt-8 grid max-w-3xl gap-5 sm:grid-cols-2">
              {[
                {
                  title: "Consulta Presencial",
                  text: "Atendimento em Maceió, com avaliação detalhada, análise de exames e orientação individualizada.",
                },
                {
                  title: "Segunda Opinião / Avaliação de Exames",
                  text: "Análise de exames, laudos e indicações prévias para auxiliar o paciente e sua família na tomada de decisão.",
                },
              ].map((c, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
                  <h4 className="font-sans font-bold text-xl text-foreground">{c.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <CtaWhats />
            </div>
          </div>
        </section>

        {/* ===== BLOCO 06 — BLOG ===== */}
        <section id="blog" className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-primary">Conteúdo</p>
            <h2 className="mt-3 font-display text-4xl text-foreground md:text-5xl">Blog</h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {blogSummaries.map((a) => (
              <article key={a.slug} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
                <img src={a.image} alt={a.title} width={1024} height={768} loading="lazy" className="aspect-[16/10] w-full object-cover" />
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-[0.68rem] uppercase tracking-[0.2em] text-rose">{a.category}</span>
                  <h3 className="mt-2 font-sans font-bold text-2xl leading-tight text-foreground">{a.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{a.excerpt}</p>
                  <Link to="/blog/$slug" params={{ slug: a.slug }} className="mt-5 inline-flex items-center gap-1 text-xs font-medium uppercase tracking-[0.15em] text-primary">
                    Leia mais <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>


        {/* ===== BLOCO 07 — AGENDAMENTO ===== */}
        <AgendamentoSection />
      </main>

      {/* ===== Footer ===== */}
      <footer className="border-t border-border bg-background [text-wrap:balance]">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-5 px-6 py-10 text-center">
          <img src={logo.url} alt="Dra. Rafaela Sales" className="h-14 w-auto" />
          <p className="max-w-md text-xs leading-relaxed text-muted-foreground">
            Conteúdo de caráter informativo e educativo. Não substitui a avaliação médica individual. Em caso de
            sintomas intensos ou súbitos, procure atendimento de urgência.
          </p>
          <div className="flex items-center gap-4 text-muted-foreground">
            <a aria-label="Instagram" href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-primary">
              <Instagram className="h-5 w-5" />
            </a>
            <a aria-label="WhatsApp" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-primary">
              <WhatsappIcon className="h-5 w-5" />
            </a>
            <a aria-label="Agendamento" href="#agendamento" className="transition-colors hover:text-primary">
              <CalendarCheck className="h-5 w-5" />
            </a>
          </div>
          <p className="text-[0.7rem] uppercase tracking-[0.18em] text-muted-foreground/70">
            © {new Date().getFullYear()} Dra. Rafaela Sales · CRM-AL: 6324 · RQE: 4240
          </p>
        </div>
      </footer>
    </div>
  );
}
